import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { FaSignOutAlt } from 'react-icons/fa';
import { StoreProvider } from './contexts/StoreContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './layouts/sidebar';
import ProductTypes from './pages/ProductTypes';
import ProductCategories from './pages/ProductCategories';
import Stores from './pages/Stores';
import Locations from './pages/Locations';
import Vendors from './pages/Vendors';
import Products from './pages/Products';
import ProductsByStore from './pages/ProductByStore';
import Login from './pages/Login';
import Users from './pages/Users';
import Recipes from './pages/Recipes';
import POSData from './pages/POS';
import Inventories from './pages/Inventories';
import Preps from './pages/Preps';
import Ordering from './pages/Ordering';
import PrepByStore from './pages/PrepByStore';
import PrepOrdering from './pages/PrepOrdering';
import ViewPrepOrders from './pages/ViewPrepOrders';
import PhysicalInventory from './pages/PhysicalInventory';
import ViewOrders from './pages/ViewOrders';
import Notifications from './pages/Notifications';
import Invoices from './pages/Invoices';
import Modifiers from './pages/Modifiers';
import VarianceReport from './pages/VarianceReport';
import UsageReport from './pages/UsageReport';

// ── Layout con sidebar (admin / employee) ──────────────────────────────────
function MainLayout({ children }) {
  return (
    <div className="app-wrapper">
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

// ── Layout SIN sidebar (kitchen) ────────────────────────────────────────────
// Ocupa todo el ancho, sin navegación lateral.
// Incluye una barra superior simple solo con el botón de cerrar sesión,
// ya que este rol no tiene acceso al Sidebar (donde normalmente vive logout).
function KitchenLayout({ children }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-wrapper app-wrapper--kitchen">
      <header className="kitchen-topbar">
        <span className="kitchen-topbar-user">{user?.name || 'Kitchen'}</span>
        <button className="kitchen-logout-btn" onClick={handleLogout} title="Log out">
          <FaSignOutAlt /> Log out
        </button>
      </header>
      <main className="main-content main-content--kitchen">
        {children}
      </main>
    </div>
  );
}

// ── Rutas protegidas ─────────────────────────────────────────────────────────
function AppRoutes() {
  const { isAuthenticated, isKitchen } = useAuth();

  return (
    <Routes>
      {/* Ruta pública - Login sin sidebar */}
      <Route
        path="/login"
        element={
          isAuthenticated() ? (
            <Navigate to={isKitchen() ? '/preps' : '/productsbystore'} replace />
          ) : (
            <Login />
          )
        }
      />

      {/* ══ Rutas para rol KITCHEN — solo Pre-Batch, sin sidebar ══ */}
      {isAuthenticated() && isKitchen() && (
        <Route
          path="/*"
          element={
            <KitchenLayout>
              <Routes>
                <Route path="preps" element={<Preps />} />
                <Route path="/" element={<Navigate to="/preps" replace />} />
                {/* Cualquier otra ruta que intenten visitar, redirige a preps */}
                <Route path="*" element={<Navigate to="/preps" replace />} />
              </Routes>
            </KitchenLayout>
          }
        />
      )}

      {/* ══ Rutas para admin / employee — con sidebar completo ══ */}
      {isAuthenticated() && !isKitchen() && (
        <Route
          path="/*"
          element={
            <MainLayout>
              <Routes>
                <Route path="productsbystore" element={<ProductsByStore />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="recipes" element={<Recipes />} />
                <Route path="pos" element={<POSData />} />
                <Route path="modifiers" element={<Modifiers />} />
                <Route path="inventories" element={<Inventories />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="physical-inventory" element={<PhysicalInventory />} />
                <Route path="ordering" element={<Ordering />} />
                <Route path="prepbystore" element={<PrepByStore />} />
                <Route path="prep-ordering" element={<PrepOrdering />} />
                <Route path="view-prep-orders" element={<ViewPrepOrders />} />
                <Route path="view-orders" element={<ViewOrders />} />
                <Route path="reports/usage" element={<UsageReport />} />
                <Route path="reports/variance" element={<VarianceReport />} />
                <Route path="preps" element={<Preps />} />
                <Route path="products" element={<Products />} />
                <Route path="producttypes" element={<ProductTypes />} />
                <Route path="productcategories" element={<ProductCategories />} />
                <Route path="stores" element={<Stores />} />
                <Route path="locations" element={<Locations />} />
                <Route path="vendors" element={<Vendors />} />
                <Route path="users" element={<Users />} />
                <Route path="/" element={<Navigate to="productsbystore" replace />} />
                <Route path="*" element={<Navigate to="productsbystore" replace />} />
              </Routes>
            </MainLayout>
          }
        />
      )}

      {/* ══ No autenticado ══ */}
      {!isAuthenticated() && (
        <Route path="/*" element={<Navigate to="/login" replace />} />
      )}
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <AppRoutes />
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;